import React, { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { TimeSeriesColors } from "@intelligems/ig-graphs";
import {
  AnalyzeTinybirdResourceResponseBodyType,
  GraphConfig,
} from "@intelligems/ig-types";
import { useTheme } from "@mui/material";
import { AxisTick } from "@src/features/analytics/baseDashboard/chartComponents/AxisTick";
import {
  dataMinFormatter,
  getDataMax,
  getDataMin,
  getFilterMethod,
} from "@src/features/analytics/baseDashboard/utils";
import { useGraphValueFormatter } from "@src/features/analytics/hooks/use-graph-value-formatter";
import { getStandardMetricByAnalyticsMetricId } from "@src/features/analytics/tables/analyticsAudienceTable/utils";
import { IgBarAnalyticsTooltip } from "@src/features/analytics/tooltips/IgBarAnalyticsTooltip";
import { IgBarCustomMetricAnalyticsTooltip } from "@src/features/analytics/tooltips/IgBarCustomMetricAnalyticsTooltip";
import {
  SitewideAnalyticsAudienceQueryResult,
  SitewideAnalyticsHolidayBenchmarkingQueryResult,
  SitewideAnalyticsPerformanceQueryResult,
} from "@src/features/analytics/types";
import { getShouldShowAxisDecimals } from "@src/features/analytics/utils";
import { BarMetricData } from "@src/types/analytics";
import { UseQueryResult } from "@tanstack/react-query";

import { BarLabel } from "../chartComponents/BarLabel";

interface IgBarChartProps {
  groupMetricData: BarMetricData[];
  config: GraphConfig;
  totalTestVisitors: number;
  filterMethod?: string;
  isSharing?: boolean;
  syncId?: string;
  focusGroups?: string[];
  margin?: { top?: number; bottom?: number; right?: number; left?: number };
  testDuration: number;
  compareVariationId?: string;
  graphHeight?: number;
  setCellColor?: (params: {
    compareVariationId?: string;
    data: BarMetricData;
    index: number;
  }) => string;
  rawData:
    | UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[]
    | SitewideAnalyticsAudienceQueryResult
    | SitewideAnalyticsPerformanceQueryResult
    | SitewideAnalyticsHolidayBenchmarkingQueryResult;
}

const findDataMaxWPadding = (dataArr: BarMetricData[]): number => {
  const dataMax = getDataMax(dataArr);

  const offset = dataMax * 0.1;
  const combined = offset + dataMax;
  return combined;
};

export const IgBarChart = ({
  groupMetricData,
  config,
  filterMethod,
  syncId,
  focusGroups,
  margin,
  compareVariationId,
  rawData,
  graphHeight,
  setCellColor,
}: IgBarChartProps) => {
  const graphValueFormatter = useGraphValueFormatter();
  const theme = useTheme();
  const isCustomMetric =
    config.isCustomMetric ??
    getStandardMetricByAnalyticsMetricId(config.metrics[0]) === undefined;
  const focusGroupedData = useMemo(() => {
    if (!focusGroups || focusGroups.length === 0) {
      return groupMetricData;
    } else {
      return groupMetricData.filter(
        (groupData) => !focusGroups.includes(groupData.variation_id)
      );
    }
  }, [focusGroups, groupMetricData]);

  const filteredData = filterMethod
    ? getFilterMethod(focusGroupedData, filterMethod)
    : focusGroupedData;

  const allData =
    !filterMethod || filterMethod === "none" ? focusGroupedData : filteredData;

  const paddedDataMx = findDataMaxWPadding(allData);
  const dataMax = paddedDataMx;
  const dataMin = dataMinFormatter(getDataMin(allData));

  const shouldShowAxisDecimals = getShouldShowAxisDecimals(allData, config);

  const tickCount = 5;
  const step = dataMax / (tickCount - 1);

  const customTicks = Array.from({ length: tickCount }, (_, i) => +(i * step));

  const shouldShowLabelDecimals =
    Math.min(
      ...groupMetricData.filter((d) => d.value !== 0).map((d) => d.value * 100)
    ) < 0.09;

  return (
    <ResponsiveContainer
      height={graphHeight ?? (config.isImpactGraph ? 150 : 300)}
      width="100%"
    >
      <BarChart
        syncId={syncId}
        margin={
          margin ? { ...margin } : { top: 5, bottom: 0, right: -5, left: -20 }
        }
        data={allData}
        maxBarSize={60}
        layout="horizontal"
      >
        <CartesianGrid
          style={{ stroke: theme.igBorders.core.disabled }}
          vertical={false}
        />

        <XAxis
          interval={0}
          tick={(props) => (
            <AxisTick
              config={config}
              graphValueFormatter={graphValueFormatter}
              groupMetricData={allData}
              {...props}
            />
          )}
          axisLine={{ stroke: theme.igBorders.core.disabled }}
          tickLine={false}
          dataKey={"name"}
          padding={{ right: 25 }}
        />

        <YAxis
          ticks={customTicks}
          interval={0}
          domain={[dataMin, dataMax]}
          style={{ fontFamily: theme.typography.fontFamily, fontSize: 10 }}
          axisLine={
            config.isImpactGraph
              ? false
              : { stroke: theme.igBorders.core.disabled }
          }
          tickLine={false}
          tickCount={5}
          tickFormatter={(value: any) => {
            return graphValueFormatter({
              isAxisTick: true,
              value,
              configDataTypes: config.dataTypes,
              decimalAmountShown: shouldShowAxisDecimals ? 2 : 1,
              shouldShorten: true,
            })!;
          }}
        />
        <Tooltip
          position={{ y: 145 }}
          content={
            isCustomMetric ? (
              <IgBarCustomMetricAnalyticsTooltip />
            ) : (
              <IgBarAnalyticsTooltip
                config={config}
                rawData={rawData?.[0]?.data?.metrics || []}
              />
            )
          }
          cursor={{ fill: "transparent" }}
          wrapperStyle={{ zIndex: 99999 }}
        />
        <Bar
          isAnimationActive={false}
          dataKey="value"
          name={config.title?.[0] || ""}
          radius={[3, 3, 0, 0]}
        >
          <LabelList
            content={(props: any) => {
              return (
                // @ts-ignore
                <BarLabel
                  props={props}
                  config={config}
                  graphValueFormatter={graphValueFormatter}
                  compareGroupA={undefined}
                  compareGroupB={undefined}
                  decimalAmountShown={
                    config.dataTypes[0] === "percentTwoDecimal" &&
                    shouldShowLabelDecimals
                      ? 3
                      : shouldShowAxisDecimals
                        ? 2
                        : 1
                  }
                />
              );
            }}
            formatter={(value) =>
              graphValueFormatter({
                isAxisTick: true,
                value,
                configDataTypes: config.dataTypes,
              })!
            }
          />

          {allData?.map((group: BarMetricData, index: number) => (
            <Cell
              key={`cell-${index}`}
              fill={
                setCellColor
                  ? setCellColor({ compareVariationId, data: group, index })
                  : group.variation_id === compareVariationId
                    ? theme.igPrimitives.base[800]
                    : config.isImpactGraph
                      ? theme.igPrimitives.base[500]
                      : TimeSeriesColors[index]
              }
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};
